import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Plus, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { HeadlineOverlay, HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';
import { SortableHeadlineVariationCard } from '../SortableHeadlineVariationCard';
import { HeadlineVariationCard } from '../HeadlineVariationCard';
import { HeadlineVariationSheet } from '../HeadlineVariationSheet';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import { nanoid } from 'nanoid';

interface HeadlineABVariationsStepProps {
  variations: HeadlineOverlayVariation[];
  headlineOverlay: HeadlineOverlay;
  onUpdateConfig: (updates: Partial<HeadlineOverlay['abTestConfig']>) => void;
  getTrafficPercentage: (variationId: string) => number;
}

export function HeadlineABVariationsStep({
  variations,
  headlineOverlay,
  onUpdateConfig,
  getTrafficPercentage
}: HeadlineABVariationsStepProps) {
  useLanguage();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingVariation, setEditingVariation] = useState<HeadlineOverlayVariation | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [variationToDelete, setVariationToDelete] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleSaveVariation = (variationData: Omit<HeadlineOverlayVariation, 'id' | 'metrics'>) => {
    if (editingVariation) {
      const updatedVariations = variations.map(v =>
        v.id === editingVariation.id ? { ...v, ...variationData } : v
      );
      onUpdateConfig({ variations: updatedVariations });
      toast.success(translateInterfaceText("Variação atualizada!"));
    } else {
      const newVariation: HeadlineOverlayVariation = {
        id: nanoid(),
        ...variationData,
        metrics: {
          views: 0, uniqueViews: 0, plays: 0, uniquePlays: 0,
          playRate: 0, engagement: 0, pitchRetention: 0,
          clicks: 0, ctr: 0, avgDisplayTime: 0,
          conversions: 0, conversionRate: 0, revenue: 0,
          platform: 'Todas'
        }
      };
      onUpdateConfig({ variations: [...variations, newVariation] });
      toast.success(translateInterfaceText("Variação adicionada!"));
    }
    setEditingVariation(undefined);
  };

  const handleEditVariation = (variation: HeadlineOverlayVariation) => {
    setEditingVariation(variation);
    setSheetOpen(true);
  };

  const handleOpenDeleteModal = (variationId: string) => {
    setVariationToDelete(variationId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (variationToDelete) {
      const updatedVariations = variations.filter(v => v.id !== variationToDelete);
      onUpdateConfig({ variations: updatedVariations });
      toast.success(translateInterfaceText("Variação removida!"));
    }
    setDeleteModalOpen(false);
    setVariationToDelete(null);
  };

  const handleDragStart = (event: { active: { id: string | number } }) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (over && active.id !== over.id) {
      const oldIndex = variations.findIndex(v => v.id === active.id);
      const newIndex = variations.findIndex(v => v.id === over.id);
      const reorderedVariations = arrayMove(variations, oldIndex, newIndex);
      onUpdateConfig({ variations: reorderedVariations });
      toast.success(translateInterfaceText("Ordem atualizada"));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header com ação */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{translateInterfaceText("Variações")}</span>
        <Button 
          variant="outline" 
          size="sm"
          className="gap-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all"
          onClick={() => {
            setEditingVariation(undefined);
            setSheetOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          {translateInterfaceText("Adicionar Variação")}</Button>
      </div>

      {/* Content */}
      <div>
        {variations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 rounded-full bg-muted/30 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-medium text-foreground">{translateInterfaceText("Crie sua primeira variação")}</p>
            <Button 
              className="mt-4 gap-2"
              onClick={() => {
                setEditingVariation(undefined);
                setSheetOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              {translateInterfaceText("Criar Variação")}</Button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={variations.map(v => v.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {variations.map((variation, index) => (
                  <SortableHeadlineVariationCard
                    key={variation.id}
                    variation={variation}
                    isActive={headlineOverlay.abTestConfig.status === 'active'}
                    isPrimary={index === 0 && variations.length > 1}
                    trafficPercentage={getTrafficPercentage(variation.id)}
                    onEdit={handleEditVariation}
                    onDelete={handleOpenDeleteModal}
                    canDelete={variations.length > 1}
                  />
                ))}
              </div>
            </SortableContext>

            <DragOverlay>
              {activeId ? (
                <div className="opacity-90">
                  <HeadlineVariationCard
                    variation={variations.find(v => v.id === activeId)!}
                    trafficPercentage={getTrafficPercentage(activeId)}
                    onEdit={() => {}}
                    onDelete={() => {}}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        {/* Hint for minimum variations */}
        {variations.length === 1 && (
          <div className="mt-3 flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="text-sm">{translateInterfaceText("Mínimo: 2 variações")}</span>
          </div>
        )}
      </div>

      {/* Variation Sheet */}
      <HeadlineVariationSheet
        open={sheetOpen}
        onClose={() => {
          setSheetOpen(false);
          setEditingVariation(undefined);
        }}
        onSave={handleSaveVariation}
        existingVariation={editingVariation}
        currentConfig={headlineOverlay}
      />

      {/* Delete Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent className="w-[92vw] sm:w-[clamp(360px,38vw,440px)] sm:max-w-none rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Excluir variação?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("Tem certeza que deseja excluir esta variação? Esta ação não pode ser desfeita.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {translateInterfaceText("Cancelar")}</Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              {translateInterfaceText("Excluir")}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
